import { ReviewDataService } from './review-data.service';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  ActionsEnum,
  fetchEligibleClientsError,
  fetchEligibleClientsSuccess,
  fetchReviewDetailError,
  fetchReviewDetailSuccess,
  fetchReviewsError,
  fetchReviewSettingsError,
  fetchReviewSettingsSuccess,
  fetchReviewsSuccess,
  fetchReviewSummaryError,
  fetchReviewSummarySuccess,
  IFetchEligibleClientsError,
  IFetchEligibleClientsSuccess,
  IFetchReviewDetail,
  IFetchReviewDetailError,
  IFetchReviewDetailSuccess,
  IFetchReviewsError,
  IFetchReviewSettingsError,
  IFetchReviewSettingsSuccess,
  IFetchReviewsSuccess,
  IFetchReviewSummaryError,
  IFetchReviewSummarySuccess,
  IISaveReviewSettingsNetworkError,
  IRequestReview,
  IRequestReviewError,
  IRequestReviewSuccess,
  ISaveReviewSettingsNetwork,
  ISaveReviewSettingsNetworkSuccess,
  ISendRequestReview,
  ISendRequestReviewError,
  ISendRequestReviewSuccess,
  requestReviewError,
  requestReviewSuccess,
  saveReviewSettingsNetworkError,
  saveReviewSettingsNetworkSuccess,
  sendRequestReviewError,
  sendRequestReviewSuccess,
} from './review-data.actions';
import {
  catchError,
  delay,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ReviewDataBuilder } from './review-data.builder';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { NavigationService } from '../navigation/navigation.service';
import { REVIEW_REQUEST_PATH } from '../settings/settings.data';
import { Action } from '@ngrx/store';
import { I2gDialogService } from 'i2g-ng-ui';
import { IReviewSettings } from './review-data.data';

@Injectable()
export class ReviewDataEffects {
  constructor(
    private _actions: Actions,
    private _reviewDataService: ReviewDataService,
    private _reviewDataBuilder: ReviewDataBuilder,
    private _globalBannerService: GlobalBannerService,
    private _navigationService: NavigationService,
    private _dialogService: I2gDialogService
  ) {}

  @Effect()
  fetchReviews(): Observable<IFetchReviewsSuccess | IFetchReviewsError> {
    return this._actions.pipe(
      ofType(ActionsEnum.FETCH_REVIEWS),
      switchMap(() => {
        return this._reviewDataService.fetchReviews().pipe(
          map((reviews) => fetchReviewsSuccess(reviews)),
          catchError((error) => of(fetchReviewsError(error)))
        );
      })
    );
  }

  @Effect()
  fetchEligibleClients(): Observable<
    IFetchEligibleClientsSuccess | IFetchEligibleClientsError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.FETCH_ELIGIBLE_CLIENTS),
      switchMap(() => {
        return this._reviewDataService.fetchEligibleClients().pipe(
          map((clients) => fetchEligibleClientsSuccess(clients)),
          catchError((error) => of(fetchEligibleClientsError(error)))
        );
      })
    );
  }

  @Effect()
  fetchReviewSummary(): Observable<
    IFetchReviewSummarySuccess | IFetchReviewSummaryError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.FETCH_REVIEW_SUMMARY),
      switchMap(() => {
        return this._reviewDataService.fetchReviewSummary().pipe(
          map((reviewSummary) => fetchReviewSummarySuccess(reviewSummary)),
          catchError((error) => of(fetchReviewSummaryError(error)))
        );
      })
    );
  }

  @Effect()
  fetchReviewDetail(): Observable<
    IFetchReviewDetailSuccess | IFetchReviewDetailError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.FETCH_REVIEW_DETAIL),
      switchMap(({ payload }: IFetchReviewDetail) => {
        return this._reviewDataService.fetchReviewDetail(payload.reviewId).pipe(
          map((review) => fetchReviewDetailSuccess(review)),
          catchError((error) => of(fetchReviewDetailError(error)))
        );
      })
    );
  }

  @Effect()
  requestReview(): Observable<IRequestReviewSuccess | IRequestReviewError> {
    return this._actions.pipe(
      ofType(ActionsEnum.REQUEST_REVIEW),
      switchMap(({ payload }: IRequestReview) => {
        return this._reviewDataService.requestReview(payload.clientId).pipe(
          map((request) => requestReviewSuccess(request)),
          catchError((error) => of(requestReviewError(error)))
        );
      })
    );
  }

  @Effect()
  sendRequestReview(): Observable<
    ISendRequestReviewSuccess | ISendRequestReviewError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.SEND_REQUEST_REVIEW),
      switchMap(({ payload }: ISendRequestReview) => {
        const requestPayload = this._reviewDataBuilder.buildSendRequestReviewRequestPayload(
          payload
        );

        return this._reviewDataService.sendRequestReview(requestPayload).pipe(
          map(() => sendRequestReviewSuccess()),
          catchError((error) => of(sendRequestReviewError(error)))
        );
      })
    );
  }

  @Effect({ dispatch: false })
  sendRequestReviewSuccess(): Observable<Action> {
    return this._actions.pipe(
      ofType(ActionsEnum.SEND_REQUEST_REVIEW_SUCCESS),
      tap(() => {
        this._navigationService.goBack(REVIEW_REQUEST_PATH);
      })
    );
  }

  @Effect({ dispatch: false })
  errorExists(): Observable<Action> {
    return this._actions.pipe(
      ofType(
        ActionsEnum.SEND_REQUEST_REVIEW_ERROR,
        ActionsEnum.FETCH_ELIGIBLE_CLIENTS_ERROR,
        ActionsEnum.REQUEST_REVIEW_ERROR
      ),
      tap(
        ({
          payload,
        }:
          | IRequestReviewError
          | ISendRequestReviewError
          | IFetchEligibleClientsError) => {
          this._globalBannerService.showSystemErrorBanner(payload.error);
        }
      )
    );
  }

  @Effect()
  fetchReviewSettings(): Observable<
    IFetchReviewSettingsSuccess | IFetchReviewSettingsError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.FETCH_REVIEW_SETTINGS),
      switchMap(() => {
        return this._reviewDataService.fetchReviewSettings().pipe(
          map((reviewSettings: IReviewSettings) => {
            return fetchReviewSettingsSuccess(reviewSettings);
          }),
          catchError((error) => {
            this._globalBannerService.showSystemErrorBanner(error);
            return of(fetchReviewSettingsError(error));
          })
        );
      })
    );
  }

  @Effect()
  saveReviewSettings(): Observable<
    ISaveReviewSettingsNetworkSuccess | IISaveReviewSettingsNetworkError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.SAVE_REVIEW_SETTINGS_NETWORK),
      switchMap(({ payload }: ISaveReviewSettingsNetwork) => {
        const requestPayload = this._reviewDataBuilder.buildReviewSettingsRequestPayload(
          payload
        );
        return this._reviewDataService
          .saveReviewSettingsNetwork(requestPayload)
          .pipe(
            map((reviewSettings: IReviewSettings) => {
              this._dialogService.openLoadingDialog();
              return saveReviewSettingsNetworkSuccess(reviewSettings);
            }),
            delay(500), // delay for enable smooth dialog opening and closing transition
            finalize(() => this._dialogService.closeDialog()),
            catchError((error) => {
              this._globalBannerService.showSystemErrorBanner(error);
              return of(saveReviewSettingsNetworkError(error));
            })
          );
      })
    );
  }
}
